import React, { useEffect, useState } from 'react';
import { useWorkflow } from '../context/WorkflowContext';
import { fetchEntityResolution, fetchCreditMemo } from '../services/osintService';
import { saveSearch } from '../api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlobalRadar from './GlobalRadar';
import CyberLoading from './CyberLoading';

const { FiChevronRight, FiGlobe, FiShield, FiAlertTriangle, FiCheck, FiSearch, FiDatabase, FiLink, FiRefreshCw } = FiIcons;

const CONFIDENCE_COLOR = (score) => {
  if (score >= 90) return 'text-sonar-green border-sonar-green bg-sonar-green/10';
  if (score >= 70) return 'text-amber-400 border-amber-400 bg-amber-400/10';
  return 'text-red-400 border-red-400 bg-red-400/10';
};

const getOmenSteps = (candidate) => {
  return [
    `✦ Intelligence Target Confirmed: ${candidate?.legalName || 'Target Entity'}`,
    '✦ Routing query to verified institutional data networks...',
    '✦ Phase 1: Executing live retrieval (multi-vector search protocol)...',
    '✦ Retrieving multi-year financial data — zero-memory mode active...',
    '✦ Phase 2: Cross-referencing disparate OSINT signals...',
    '✦ Applying proprietary validation and confidence scoring...',
    '✦ Screening global sanctions and regulatory enforcement databases...',
    '✦ Executing comprehensive credit rating agency hierarchy search...',
    '✦ Analyzing real-time sentiment and market signals...',
    '✦ Phase 3: Final synthesis — compiling intelligence package...',
    '✦ Validating output against source-integrity guardrails...',
  ];
};

const EntityResolutionPage = ({ user }) => {
  const {
    companyName, location, apiKey, provider, selectedModel,
    setPhase, setMarkdownReport, reportLayout,
  } = useWorkflow();

  const [isResolving, setIsResolving] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingStep, setGeneratingStep] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => { resolve(); }, []);

  const resolve = async () => {
    setIsResolving(true);
    setError('');
    try {
      const data = await fetchEntityResolution(companyName, location, apiKey, provider, selectedModel);
      const cands = data.candidates || [];
      setCandidates(cands);
      if (cands.length === 1) setSelectedId(cands[0].id);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsResolving(false);
    }
  };

  const handleProceed = async () => {
    const candidate = candidates.find((c) => c.id === selectedId);
    if (!candidate) return;
    setIsGenerating(true);
    setGeneratingStep(0);

    const steps = getOmenSteps(candidate);
    const stepInterval = setInterval(() => {
      setGeneratingStep((p) => Math.min(p + 1, steps.length - 1));
    }, 2800);

    try {
      const markdown = await fetchCreditMemo(apiKey, provider, selectedModel, candidate, reportLayout);
      setMarkdownReport(markdown);
      clearInterval(stepInterval);

      if (user) {
        try {
          await saveSearch(user, companyName, location, markdown);
        } catch (saveErr) {
          console.warn('Auto-save failed:', saveErr.message);
        }
      }
      setPhase(3);
    } catch (err) {
      setError(err.message);
      setIsGenerating(false);
      clearInterval(stepInterval);
    }
  };

  if (isGenerating) {
    const candidate = candidates.find((c) => c.id === selectedId);
    const steps = getOmenSteps(candidate);

    return (
      <div className="w-full py-10 px-4 max-w-4xl mx-auto">
        <div className="mb-8 text-center text-sonar-green">
          <div className="inline-flex items-center gap-2 bg-sonar-green/10 px-4 py-1.5 rounded-full text-[10px] font-bold mb-4 border border-sonar-green/30 tracking-widest uppercase">
            <div className="w-2 h-2 rounded-full bg-sonar-green animate-pulse" />
            OMEN v4 — AGENT ACTIVE
          </div>
          <h2 className="text-xl font-black text-white uppercase tracking-widest mb-1">Generating Intelligence Memo</h2>
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            3-Phase Verification Protocol
          </p>
        </div>

        <CyberLoading />

        <div className="mt-6 bg-sonar-card border border-sonar-border rounded-xl p-5 max-w-2xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <SafeIcon icon={FiDatabase} className="text-sonar-green text-sm" />
            <span className="text-[10px] font-black uppercase tracking-widest text-sonar-green">System Execution Log</span>
          </div>
          <div className="space-y-2">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 text-[10px] font-mono py-1.5 px-3 rounded-lg transition-all ${
                  i < generatingStep
                    ? 'text-slate-500 bg-transparent'
                    : i === generatingStep
                    ? 'text-sonar-green bg-sonar-green/5 border border-sonar-green/20'
                    : 'text-slate-700'
                }`}
              >
                <span className="shrink-0 w-4 text-center font-bold">
                  {i < generatingStep ? <SafeIcon icon={FiCheck} className="text-sonar-green text-[9px]" /> : i === generatingStep ? '›' : '○'}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isResolving) {
    return (
      <div className="w-full py-10 px-4 max-w-4xl mx-auto">
        <GlobalRadar label={`Resolving Intelligence Target: ${companyName}...`} />
      </div>
    );
  }

  if (error && candidates.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-2xl" />
        </div>
        <h2 className="text-xl font-black text-white mb-3 uppercase tracking-widest">System Error</h2>
        <p className="text-red-400 font-mono text-sm mb-6 leading-relaxed">{error}</p>
        <button
          onClick={resolve}
          className="flex items-center gap-2 mx-auto bg-sonar-green text-black px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-sonar-green-glow transition-all"
        >
          <SafeIcon icon={FiRefreshCw} /> Retry System Query
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 bg-sonar-green/10 text-sonar-green px-3 py-1 rounded-full text-[10px] font-bold border border-sonar-green/30 tracking-widest uppercase mb-3">
          <SafeIcon icon={FiGlobe} /> Target Resolution Complete
        </div>
        <h1 className="text-2xl font-black text-white uppercase tracking-widest mb-1">
          Confirm Target Entity
        </h1>
        <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
          The system has identified these legal candidates. Confirm the correct entity to begin intelligence synthesis.
        </p>
      </div>

      <div className="space-y-4 mb-8">
        {candidates.map((c) => (
          <div
            key={c.id}
            onClick={() => setSelectedId(c.id)}
            className={`p-6 bg-sonar-card border rounded-xl cursor-pointer transition-all ${
              selectedId === c.id
                ? 'border-sonar-green bg-sonar-green/5 shadow-[0_0_20px_rgba(46,204,113,0.1)]'
                : 'border-sonar-border hover:border-sonar-green/50'
            }`}
          >
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="text-white font-bold text-lg">{c.legalName}</h3>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-widest border bg-sonar-green/10 text-sonar-green border-sonar-green/30">
                    {c.primarySource}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 font-mono mb-2">
                  {c.jurisdiction} • Functional Currency: <span className="text-sonar-green font-bold">{c.functionalCurrency}</span>
                </div>
                <p className="text-xs text-slate-400 font-mono leading-relaxed">{c.notes}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">Confidence</div>
                <div className={`text-2xl font-black font-mono px-3 py-1 rounded-xl border ${CONFIDENCE_COLOR(c.confidence)}`}>
                  {c.confidence}%
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-sonar-border">
        <button onClick={() => setPhase(0)} className="text-xs text-slate-400 hover:text-white font-mono uppercase tracking-widest transition-colors">
          Home
        </button>
        <button
          disabled={!selectedId}
          onClick={handleProceed}
          className="flex items-center gap-2 bg-sonar-green text-black px-10 py-4 rounded-xl font-black uppercase text-xs disabled:opacity-30 hover:bg-sonar-green-glow transition-all shadow-[0_0_15px_rgba(46,204,113,0.2)]"
        >
          Synthesize Memo <SafeIcon icon={FiChevronRight} />
        </button>
      </div>
    </div>
  );
};

export default EntityResolutionPage;